import { useState, useEffect, useRef, useCallback } from 'react';
import { detectTopic, TOPIC_STYLE } from '../topicUtils';
import { NotebookEntry } from './NotebookPanel';

// ─── Graph data ───────────────────────────────────────────────────────────────

type Category = 'calculus' | 'algebra' | 'trig' | 'stats' | 'physics' | 'notebook';

interface MapNode {
  id: string;
  label: string;
  category: Category;
  ix: number; // initial x
  iy: number; // initial y
  desc: string;
  example: string;
}

const CAT_COLORS: Record<Category, { fill: string; stroke: string; text: string; glow: string }> = {
  calculus: { fill: '#7c3aed', stroke: '#6d28d9', text: '#ffffff', glow: 'rgba(124,58,237,0.35)' },
  algebra:  { fill: '#059669', stroke: '#047857', text: '#ffffff', glow: 'rgba(5,150,105,0.35)' },
  trig:     { fill: '#d97706', stroke: '#b45309', text: '#ffffff', glow: 'rgba(217,119,6,0.35)' },
  stats:    { fill: '#e11d48', stroke: '#be185d', text: '#ffffff', glow: 'rgba(225,29,72,0.35)' },
  physics:  { fill: '#0891b2', stroke: '#0e7490', text: '#ffffff', glow: 'rgba(8,145,178,0.35)' },
  notebook: { fill: '#64748b', stroke: '#475569', text: '#ffffff', glow: 'rgba(100,116,139,0.35)' },
};

const CAT_CENTERS: Record<Category, { x: number; y: number }> = {
  calculus: { x: 290, y: 220 },
  algebra:  { x: 620, y: 200 },
  trig:     { x: 660, y: 450 },
  stats:    { x: 220, y: 460 },
  physics:  { x: 460, y: 400 },
  notebook: { x: 450, y: 290 },
};

const BASE_NODES: MapNode[] = [
  // Calculus
  { id: 'limits',       label: 'Limits',          category: 'calculus', ix: 180, iy: 180, desc: 'Behavior of a function as x approaches a value.',       example: 'limit as x->0 of sin(x)/x' },
  { id: 'derivatives',  label: 'Derivatives',     category: 'calculus', ix: 310, iy: 155, desc: 'Rate of change — instantaneous slope of a curve.',      example: 'derivative of x^3 * sin(x)' },
  { id: 'chain_rule',   label: 'Chain Rule',      category: 'calculus', ix: 445, iy: 120, desc: "d/dx f(g(x)) = f'(g(x))·g'(x) — composite functions.", example: 'derivative of sin(x^2)' },
  { id: 'product_rule', label: 'Product Rule',    category: 'calculus', ix: 430, iy: 200, desc: "(fg)' = f'g + fg' — differentiate products.",             example: 'derivative of x^2 * cos(x)' },
  { id: 'integrals',    label: 'Integrals',       category: 'calculus', ix: 240, iy: 300, desc: 'Area under a curve — antiderivative of a function.',     example: 'integrate x^2 from 0 to 3' },
  { id: 'taylor',       label: 'Taylor Series',   category: 'calculus', ix: 375, iy: 285, desc: 'Power series expansion: Σ f⁽ⁿ⁾(a)/n! · (x-a)ⁿ',         example: 'Taylor series of e^x at x=0' },
  { id: 'lhopital',     label: "L'Hôpital",      category: 'calculus', ix: 130, iy: 270, desc: 'Evaluate 0/0 or ∞/∞ limits using derivatives.',          example: 'limit as x->0 of sin(x)/x' },
  // Algebra
  { id: 'functions',    label: 'Functions',       category: 'algebra',  ix: 565, iy: 125, desc: 'A rule that maps each input to exactly one output.',     example: 'plot x^3 - 2x' },
  { id: 'equations',    label: 'Equations',       category: 'algebra',  ix: 645, iy: 195, desc: 'Find values of x satisfying f(x) = g(x).',              example: 'solve x^2 - 5x + 6 = 0' },
  { id: 'quadratic',    label: 'Quadratic',       category: 'algebra',  ix: 630, iy: 290, desc: 'x = (−b ± √(b²−4ac)) / 2a',                            example: 'solve 2x^2 + 3x - 5 = 0' },
  { id: 'factoring',    label: 'Factoring',       category: 'algebra',  ix: 740, iy: 155, desc: 'Write a polynomial as a product of simpler factors.',    example: 'factor x^3 - 6x^2 + 11x - 6' },
  { id: 'logarithms',   label: 'Logarithms',      category: 'algebra',  ix: 510, iy: 220, desc: 'Inverse of exponentials: log_b(x) = y ⟺ b^y = x.',     example: 'derivative of ln(x^2 + 1)' },
  { id: 'exponentials', label: 'Exponentials',    category: 'algebra',  ix: 490, iy: 145, desc: 'Functions of the form aˣ or eˣ — constant growth rate.', example: 'derivative of e^(2x)' },
  // Trig
  { id: 'unit_circle',  label: 'Unit Circle',     category: 'trig',     ix: 600, iy: 400, desc: 'Circle of radius 1 at origin — defines sin and cos.',   example: 'plot sin(x) + cos(x)' },
  { id: 'sin_cos_tan',  label: 'sin/cos/tan',     category: 'trig',     ix: 705, iy: 455, desc: 'Core trig functions: ratios of sides in right triangles.', example: 'derivative of sin(x) * cos(x)' },
  { id: 'identities',   label: 'Trig Identities', category: 'trig',     ix: 575, iy: 490, desc: 'sin²x + cos²x = 1 and double/half angle formulas.',     example: 'simplify sin(x)^2 + cos(x)^2' },
  { id: 'inverse_trig', label: 'Inverse Trig',    category: 'trig',     ix: 700, iy: 540, desc: 'arcsin, arccos, arctan — inverse trig functions.',       example: 'derivative of atan(x)' },
  // Stats
  { id: 'mean_median',  label: 'Mean & Median',   category: 'stats',    ix: 155, iy: 420, desc: 'Central tendency: average and middle value of data.',    example: 'mean of {3, 7, 2, 9, 4, 6}' },
  { id: 'std_dev',      label: 'Std Deviation',   category: 'stats',    ix: 245, iy: 495, desc: 'σ = √(Σ(xᵢ − μ)²/n) — spread around the mean.',        example: 'standard deviation of {2,4,4,4,5,5,7,9}' },
  { id: 'normal_dist',  label: 'Normal Dist.',    category: 'stats',    ix: 145, iy: 530, desc: 'Bell curve — 68-95-99.7 rule for probability.',          example: 'normal distribution mean=0 sigma=1' },
  { id: 'probability',  label: 'Probability',     category: 'stats',    ix: 305, iy: 480, desc: 'Likelihood: P(A) between 0 and 1.',                     example: 'binomial distribution n=10 p=0.5' },
  // Physics
  { id: 'newton2',      label: "Newton's 2nd",   category: 'physics',  ix: 440, iy: 420, desc: 'F = ma — Force equals mass times acceleration.',         example: 'F = ma, m=5 a=9.8' },
  { id: 'energy',       label: 'Energy',          category: 'physics',  ix: 520, iy: 470, desc: 'KE = ½mv² and PE = mgh — forms of mechanical energy.',  example: 'kinetic energy m=2 v=30' },
];

const BASE_EDGES: { from: string; to: string; cross?: boolean }[] = [
  // Calculus
  { from: 'limits', to: 'derivatives' }, { from: 'derivatives', to: 'integrals' },
  { from: 'derivatives', to: 'chain_rule' }, { from: 'derivatives', to: 'product_rule' },
  { from: 'derivatives', to: 'taylor' }, { from: 'limits', to: 'lhopital' },
  { from: 'lhopital', to: 'derivatives', cross: true },
  // Algebra
  { from: 'exponentials', to: 'logarithms' }, { from: 'exponentials', to: 'functions' },
  { from: 'functions', to: 'equations' }, { from: 'equations', to: 'quadratic' },
  { from: 'equations', to: 'factoring' },
  // Trig
  { from: 'unit_circle', to: 'sin_cos_tan' }, { from: 'sin_cos_tan', to: 'identities' },
  { from: 'identities', to: 'inverse_trig' },
  // Stats
  { from: 'mean_median', to: 'std_dev' }, { from: 'std_dev', to: 'normal_dist' },
  { from: 'normal_dist', to: 'probability' },
  // Physics
  { from: 'newton2', to: 'energy', cross: true },
  // Cross-category connections
  { from: 'derivatives', to: 'sin_cos_tan', cross: true },
  { from: 'derivatives', to: 'exponentials', cross: true },
  { from: 'derivatives', to: 'logarithms', cross: true },
  { from: 'taylor', to: 'exponentials', cross: true },
  { from: 'integrals', to: 'probability', cross: true },
  { from: 'derivatives', to: 'newton2', cross: true },
];

function findClosestNode(query: string): string {
  const q = query.toLowerCase();
  if (/derivative|d\/dx/.test(q)) return 'derivatives';
  if (/integrat/.test(q)) return 'integrals';
  if (/\blimit\b/.test(q)) return 'limits';
  if (/taylor|maclaurin/.test(q)) return 'taylor';
  if (/\bfactor\b/.test(q)) return 'factoring';
  if (/\bsolve\b/.test(q)) return 'equations';
  if (/mean|median|std|variance/.test(q)) return 'mean_median';
  if (/normal dist|binomial/.test(q)) return 'normal_dist';
  if (/\bsin\b|\bcos\b|\btan\b/.test(q)) return 'sin_cos_tan';
  if (/\blog\b|\bln\b/.test(q)) return 'logarithms';
  if (/\bexp\b|e\^/.test(q)) return 'exponentials';
  if (/kinetic|newton|momentum|ohm/.test(q)) return 'newton2';
  return 'derivatives';
}

type Pos = Record<string, { x: number; y: number }>;

function runSimulation(nodes: MapNode[], edges: { from: string; to: string; cross?: boolean }[]): Pos {
  const pos: Pos = {};
  for (const n of nodes) pos[n.id] = { x: n.ix, y: n.iy };

  for (let iter = 0; iter < 200; iter++) {
    const alpha = Math.pow(0.988, iter);
    const forces: Record<string, { fx: number; fy: number }> = {};
    for (const n of nodes) forces[n.id] = { fx: 0, fy: 0 };

    // Repulsion
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = nodes[i].id, b = nodes[j].id;
        const dx = pos[a].x - pos[b].x;
        const dy = pos[a].y - pos[b].y;
        const d2 = dx * dx + dy * dy + 1;
        const d = Math.sqrt(d2);
        const f = 2800 / d2;
        forces[a].fx += (f * dx) / d; forces[a].fy += (f * dy) / d;
        forces[b].fx -= (f * dx) / d; forces[b].fy -= (f * dy) / d;
      }
    }
    // Spring attraction along edges
    for (const e of edges) {
      if (!pos[e.from] || !pos[e.to]) continue;
      const dx = pos[e.to].x - pos[e.from].x;
      const dy = pos[e.to].y - pos[e.from].y;
      const d = Math.sqrt(dx * dx + dy * dy) || 1;
      const ideal = e.cross ? 180 : 120;
      const f = (d - ideal) * 0.03;
      forces[e.from].fx += (f * dx) / d; forces[e.from].fy += (f * dy) / d;
      forces[e.to].fx -= (f * dx) / d;   forces[e.to].fy -= (f * dy) / d;
    }
    // Category gravity
    for (const n of nodes) {
      const c = CAT_CENTERS[n.category];
      forces[n.id].fx += (c.x - pos[n.id].x) * 0.012;
      forces[n.id].fy += (c.y - pos[n.id].y) * 0.012;
    }
    // Apply with cooling
    for (const n of nodes) {
      pos[n.id] = {
        x: Math.max(70, Math.min(830, pos[n.id].x + forces[n.id].fx * alpha)),
        y: Math.max(55, Math.min(555, pos[n.id].y + forces[n.id].fy * alpha)),
      };
    }
  }
  return pos;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  show: boolean;
  onClose: () => void;
  notebookEntries: NotebookEntry[];
  onSolve: (query: string) => void;
}

export default function ConceptMap({ show, onClose, notebookEntries, onSolve }: Props) {
  const [positions, setPositions] = useState<Pos>({});
  const [hovered, setHovered] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ dx: 0, dy: 0 });
  const [simDone, setSimDone] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const [viewBox, setViewBox] = useState('0 0 900 610');
  const panRef = useRef<{ startX: number; startY: number; vb: number[] } | null>(null);

  // Build notebook nodes
  const notebookNodes: MapNode[] = notebookEntries.slice(0, 12).map((e, i) => ({
    id: `nb_${e.id}`,
    label: e.query.slice(0, 22) + (e.query.length > 22 ? '…' : ''),
    category: 'notebook' as Category,
    ix: 200 + (i % 6) * 100, iy: 580 + Math.floor(i / 6) * 60,
    desc: `Saved: ${e.query}`,
    example: e.query,
  }));

  const notebookEdges = notebookNodes.map(n => ({
    from: n.id,
    to: findClosestNode(n.example),
    cross: true,
  }));

  const allNodes = [...BASE_NODES, ...notebookNodes];
  const allEdges = [...BASE_EDGES, ...notebookEdges];

  useEffect(() => {
    if (!show || simDone) return;
    // Initialize positions for notebook nodes
    const initPos: Pos = {};
    for (const n of allNodes) initPos[n.id] = { x: n.ix, y: n.iy };
    const result = runSimulation(allNodes, allEdges);
    setPositions(result);
    setSimDone(true);
  }, [show, simDone]);

  const getSVGPoint = useCallback((e: React.MouseEvent): { x: number; y: number } => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    const [vx, vy, vw, vh] = viewBox.split(' ').map(Number);
    const sx = vx + ((e.clientX - rect.left) / rect.width) * vw;
    const sy = vy + ((e.clientY - rect.top) / rect.height) * vh;
    return { x: sx, y: sy };
  }, [viewBox]);

  const onNodeMouseDown = useCallback((e: React.MouseEvent, nodeId: string) => {
    e.stopPropagation();
    const pt = getSVGPoint(e);
    const np = positions[nodeId];
    if (!np) return;
    setDragging(nodeId);
    setDragOffset({ dx: pt.x - np.x, dy: pt.y - np.y });
  }, [positions, getSVGPoint]);

  const onSVGMouseMove = useCallback((e: React.MouseEvent) => {
    if (dragging) {
      const pt = getSVGPoint(e);
      setPositions(prev => ({ ...prev, [dragging]: { x: pt.x - dragOffset.dx, y: pt.y - dragOffset.dy } }));
    } else if (panRef.current) {
      const { startX, startY, vb } = panRef.current;
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      const dx = ((startX - e.clientX) / rect.width) * vb[2];
      const dy = ((startY - e.clientY) / rect.height) * vb[3];
      setViewBox(`${vb[0] + dx} ${vb[1] + dy} ${vb[2]} ${vb[3]}`);
    }
  }, [dragging, dragOffset, getSVGPoint]);

  const onSVGMouseUp = useCallback(() => {
    setDragging(null);
    panRef.current = null;
  }, []);

  const onSVGMouseDown = useCallback((e: React.MouseEvent) => {
    if (dragging) return;
    const vbNums = viewBox.split(' ').map(Number);
    panRef.current = { startX: e.clientX, startY: e.clientY, vb: vbNums };
  }, [dragging, viewBox]);

  const onWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const [vx, vy, vw, vh] = viewBox.split(' ').map(Number);
    const factor = e.deltaY > 0 ? 1.1 : 0.9;
    const nw = Math.min(1400, Math.max(400, vw * factor));
    const nh = Math.min(900, Math.max(250, vh * factor));
    setViewBox(`${vx} ${vy} ${nw} ${nh}`);
  }, [viewBox]);

  const hoveredNode = allNodes.find(n => n.id === hovered);
  const connectedIds = hovered ? new Set(allEdges.filter(e => e.from === hovered || e.to === hovered).flatMap(e => [e.from, e.to])) : new Set<string>();

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm transition-opacity ${show ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden flex flex-col" style={{ height: 'min(90vh, 700px)' }}>

        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 dark:from-slate-950 dark:to-slate-900 px-5 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
              <i className="ri-mind-map text-white text-sm"></i>
            </div>
            <div>
              <h3 className="text-white font-bold text-sm">Concept Map</h3>
              <p className="text-slate-400 text-xs">Drag nodes · Scroll to zoom · Pan to explore</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Legend */}
            <div className="hidden sm:flex items-center gap-2">
              {(['calculus', 'algebra', 'trig', 'stats', 'physics'] as Category[]).map(c => (
                <div key={c} className="flex items-center gap-1">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CAT_COLORS[c].fill }}></div>
                  <span className="text-slate-400 text-[9px] capitalize">{c}</span>
                </div>
              ))}
              {notebookNodes.length > 0 && (
                <div className="flex items-center gap-1">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CAT_COLORS.notebook.fill }}></div>
                  <span className="text-slate-400 text-[9px]">notebook</span>
                </div>
              )}
            </div>
            <button onClick={() => setViewBox('0 0 900 610')} className="px-2 py-1 text-[10px] bg-white/10 hover:bg-white/20 text-white rounded-lg cursor-pointer whitespace-nowrap">Reset View</button>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white cursor-pointer">
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        </div>

        {/* SVG */}
        <div className="flex-1 relative overflow-hidden bg-slate-50 dark:bg-slate-800/50">
          {!simDone && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-slate-400 text-sm">Computing layout…</div>
            </div>
          )}
          <svg
            ref={svgRef}
            viewBox={viewBox}
            className="w-full h-full select-none"
            style={{ cursor: dragging ? 'grabbing' : 'grab' }}
            onMouseMove={onSVGMouseMove}
            onMouseUp={onSVGMouseUp}
            onMouseLeave={onSVGMouseUp}
            onMouseDown={onSVGMouseDown}
            onWheel={onWheel}
          >
            <defs>
              <marker id="arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                <path d="M0,0 L6,3 L0,6 Z" fill="#94a3b8" />
              </marker>
              <filter id="glow">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>

            {/* Edges */}
            {simDone && allEdges.map((e, i) => {
              const a = positions[e.from], b = positions[e.to];
              if (!a || !b) return null;
              const isHighlighted = hovered && (e.from === hovered || e.to === hovered);
              const midX = (a.x + b.x) / 2;
              const midY = (a.y + b.y) / 2;
              const dx = b.x - a.x, dy = b.y - a.y;
              const len = Math.sqrt(dx * dx + dy * dy) || 1;
              const nx = -dy / len, ny = dx / len;
              const bend = e.cross ? 28 : 12;
              return (
                <path
                  key={i}
                  d={`M ${a.x} ${a.y} Q ${midX + nx * bend} ${midY + ny * bend} ${b.x} ${b.y}`}
                  fill="none"
                  stroke={isHighlighted ? '#6366f1' : e.cross ? '#cbd5e1' : '#94a3b8'}
                  strokeWidth={isHighlighted ? 2.5 : 1.5}
                  strokeDasharray={e.cross ? '5,4' : undefined}
                  strokeOpacity={hovered && !isHighlighted ? 0.25 : 0.8}
                  markerEnd="url(#arrow)"
                />
              );
            })}

            {/* Nodes */}
            {simDone && allNodes.map(node => {
              const p = positions[node.id];
              if (!p) return null;
              const col = CAT_COLORS[node.category];
              const isHovered = hovered === node.id;
              const isConnected = connectedIds.has(node.id);
              const isNotebook = node.category === 'notebook';
              const r = isNotebook ? 18 : 26;
              const opacity = hovered && !isHovered && !isConnected ? 0.35 : 1;

              return (
                <g
                  key={node.id}
                  transform={`translate(${p.x},${p.y})`}
                  style={{ opacity, cursor: 'pointer', transition: 'opacity 0.15s' }}
                  onMouseEnter={() => setHovered(node.id)}
                  onMouseLeave={() => setHovered(null)}
                  onMouseDown={ev => onNodeMouseDown(ev, node.id)}
                >
                  {isHovered && (
                    <circle r={r + 9} fill={col.glow} />
                  )}
                  <circle
                    r={r}
                    fill={isHovered || isConnected ? col.fill : `${col.fill}dd`}
                    stroke={isHovered ? '#ffffff' : col.stroke}
                    strokeWidth={isHovered ? 2.5 : 1.5}
                    filter={isHovered ? 'url(#glow)' : undefined}
                  />
                  <text
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={col.text}
                    fontSize={isNotebook ? 7 : 8.5}
                    fontWeight="700"
                    fontFamily="system-ui, sans-serif"
                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                  >
                    {node.label.includes(' ') ? (
                      node.label.split(' ').map((word, wi, arr) => (
                        <tspan key={wi} x="0" dy={wi === 0 ? `-${(arr.length - 1) * 5}` : '11'}>
                          {word}
                        </tspan>
                      ))
                    ) : node.label}
                  </text>
                </g>
              );
            })}
          </svg>

          {/* Tooltip */}
          {hoveredNode && (
            <div className="absolute bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-72 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 shadow-xl pointer-events-none z-10">
              <div className="flex items-start gap-2 mb-2">
                <div className="w-3 h-3 rounded-full mt-0.5 flex-shrink-0" style={{ backgroundColor: CAT_COLORS[hoveredNode.category].fill }}></div>
                <div>
                  <div className="font-bold text-slate-800 dark:text-slate-100 text-sm">{hoveredNode.label}</div>
                  <div className="text-[10px] text-slate-400 capitalize">{hoveredNode.category}</div>
                </div>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300 mb-2 leading-relaxed">{hoveredNode.desc}</p>
              <div className="flex items-center gap-2">
                <code className="text-[9px] text-slate-400 font-mono flex-1 truncate">{hoveredNode.example}</code>
                <button
                  className="pointer-events-auto flex items-center gap-1 px-2 py-1 bg-slate-800 dark:bg-white/10 hover:bg-slate-700 text-white text-[9px] font-bold rounded-lg cursor-pointer whitespace-nowrap"
                  onMouseDown={e => { e.stopPropagation(); onSolve(hoveredNode.example); onClose(); }}
                >
                  <i className="ri-play-fill"></i> Solve
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
